import { createConsumer } from '@rails/actioncable';
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';

import { getActualAuthToken } from '@/api/sendApiRequest';
import {
  logger as loggerInstance,
  turnOffLogger,
  parseJson,
} from '@/utils';
import {
  EWasAuthBy,
  ONE_SECOND,
  QUARTER_OF_MINUTE_DELAY,
} from '@/constants';
import { checkFeatureIsEnabled } from '@/domains/checkFeatureIsEnabled';
import { EExperimentalFeatures } from '@/domains/constants';
import { useTenantsStore } from '@/stores/tenants';
import { useAppStore } from '@/stores/app';

import type {
  TCable,
  TCableSubscription,
  TConsumerInstance,
  TWsChannelNameWithParams,
  TWsMessage,
  TWsSubscriptionConsumer,
  TWsSubscriptionConsumers,
  TWsSubscriptionHandlers,
} from './types';
import { getTasksStackPromise } from './tasksStack';
import { MESSAGE_REASON, MESSAGE_TYPE } from './constants';

let consumerInstance: TConsumerInstance = null;

/**
 * Хранит потребителей подписки на канал.
 * Где ключ, это идентификатор самой подписки (свойство subscription.identifier).
 * Пример ключа: {"channel":"ReportsChannel","employeeId":"0cac2e96-e43a-4eed-b015-2b5b716ad2f3"}
 */
const subscriptionConsumers: TWsSubscriptionConsumers = {};

/** Добавляет потребителя подписки на канал */
const addConsumerForSubscription = (subscriptionIdentifier: string, consumer: TWsSubscriptionConsumer) => {
  if (!subscriptionConsumers[subscriptionIdentifier]) {
    subscriptionConsumers[subscriptionIdentifier] = [consumer];
  } else {
    subscriptionConsumers[subscriptionIdentifier].push(consumer);
  }
};

/** Удаляет потребителя подписки на канал */
const removeConsumerForSubscription = (subscriptionIdentifier: string, consumerId: string) => {
  if (!subscriptionConsumers[subscriptionIdentifier]?.length) return;

  subscriptionConsumers[subscriptionIdentifier] = subscriptionConsumers[subscriptionIdentifier].filter(
    ({ id }: TWsSubscriptionConsumer) => id !== consumerId,
  );

  // если потребителей для соответствующей подписки больше нет, то удаляем ключ
  if (!subscriptionConsumers[subscriptionIdentifier].length) {
    delete subscriptionConsumers[subscriptionIdentifier];
  }
};

/** Производит сброс внутреннего состояния всех потребителей данной подписки на канал: сброс параметров, отписка от eventBus */
const resetConsumersForSubscription = (subscriptionIdentifier: string) => {
  // если есть потребители подписки, производит сброс их внутреннего состояния
  if (subscriptionConsumers[subscriptionIdentifier]) {
    subscriptionConsumers[subscriptionIdentifier].forEach(({ reset }: TWsSubscriptionConsumer) => {
      reset();
    });

    delete subscriptionConsumers[subscriptionIdentifier];
  }
};

const logger = import.meta.env.VITE_APP_WEBSOCKET_LOGGER_ENABLED === 'true'
  ? loggerInstance
  : turnOffLogger(loggerInstance);

const baseWsHost = import.meta.env.VITE_APP_WS_HOST || null;

export const getCurrentTenantId = (): string => useTenantsStore().currentTenant?.id || '';
const getFieldName = (tenantId?: string) => (!tenantId ? 'tenantId' : 'token');

/**
 * Возвращает дополнительный параметр (employee Id) для установки подключения к сокетам.
 * Значение возвращется только в случае, если пользователь вошел в ЛК с временным доступом,
 * в противном случае вернется null.
 * */
export const getEmployeeIdForWsConnectUrl = () => {
  const isAuthByTemporaryAccessSession = useAppStore().wasAuthBy === EWasAuthBy.temporaryAccessSession;

  return isAuthByTemporaryAccessSession
    ? useTenantsStore().currentTenant?.employee.id || null
    : null;
};

export const getUrl = ({
  tenantId,
  token,
  employeeId,
}: { tenantId: string, token: string, employeeId: string | null }): string => {
  if (!baseWsHost) {
    logger.warn('[getUrl] baseWsHost is empty! Looks like you did not provide value for VITE_APP_WS_HOST env variable.');
    return '';
  }

  if (!tenantId || !token) {
    logger.warn(`[handleMessageEvent reconnect] ${getFieldName(tenantId)} is undefined.`);
    return '';
  }

  const url = `${baseWsHost}/cable?token=${token}&tenantId=${tenantId}`;

  return employeeId ? `${url}&employeeId=${employeeId}` : url;
};

/**
 * Больше информации
 * docs/development/implementation-details/websockets.md
 */

export const connectConsumer = async () => {
  const consumer = await getConsumerInstance();

  if (consumer) {
    consumer.connect();
  }
};

export const connectConsumerWithDebounce = debounce(connectConsumer, ONE_SECOND * 3);

export const closeConnection = async () => {
  const consumer = await getConsumerInstance();

  if (consumer) {
    /**
     * Метод close ниже, принимает аргумент allowReconnect (если не передавать, по умолчанию { allowReconnect: true }).
     *
     * При значении true, через 1 минуту, ConnectionMonitor (из библиотеки actionCable) попытается переподключиться к сокетам (по старому урлу и получим disconnect снова).
     */

    consumer.connection.close({ allowReconnect: false });
  }
};

const checkAuthTokenWithThrottle = throttle(() => {
  getActualAuthToken().catch(() => {});
}, QUARTER_OF_MINUTE_DELAY, {
  leading: true,
  trailing: false,
});

export const handleMessageEvent = (consumer: TConsumerInstance, eventParsed: TWsMessage) => {
  if (!consumer) return Promise.reject();

  const isRefactoringWebsocketControllerAvailable = checkFeatureIsEnabled(EExperimentalFeatures.refactoringWebsocketController);

  const { type, reason } = eventParsed;

  // В данном блоке проверяем актуальность токена
  if (type === MESSAGE_TYPE.ping) {
    checkAuthTokenWithThrottle();

    return Promise.resolve();
  }

  // если бэк говорит, что мы не авторизованы, то обновляем токен и снова подключаемся
  if (type === MESSAGE_TYPE.disconnect && reason === MESSAGE_REASON.unauthorized) {
    logger.warn('Connection is unauthorized. Disconnect.');

    closeConnection();
    if (isRefactoringWebsocketControllerAvailable) return Promise.reject(eventParsed);

    // После выпила фф refactoringWebsocketController удалить весь код ниже до конца ближайшего if
    getActualAuthToken()
      .then((tokens: any) => {
        if (!consumer) return;

        const accessToken = tokens?.accessToken || '';
        const tenantId = getCurrentTenantId();

        if (!accessToken || !tenantId) {
          logger.warn(`[handleMessageEvent reconnect] ${getFieldName(tenantId)} is undefined.`);
          return;
        }

        consumer._url = getUrl({
          tenantId,
          token: accessToken,
          employeeId: getEmployeeIdForWsConnectUrl(),
        });

        connectConsumerWithDebounce();
      })
      .catch(() => {});

    return Promise.reject(eventParsed);
  }

  return Promise.resolve();
};

/**
 * [getConsumerInstance] Через singleton создаётся единственный потребитель на всё приложение
 * Здесь же происходит подписка на входящие сообщения
 */
export const getConsumerInstance = async (): Promise<TCable> => {
  if (consumerInstance) return consumerInstance;

  try {
    const tokens = await getActualAuthToken() as any;
    const tenantId = getCurrentTenantId();
    const url = getUrl({
      tenantId,
      token: tokens?.accessToken || '',
      employeeId: getEmployeeIdForWsConnectUrl(),
    });

    if (!consumerInstance && url) {
      consumerInstance = createConsumer(url) as TCable;

      const initialMessageEvent = consumerInstance.connection.events.message;

      consumerInstance.connection.events.message = (event: any) => {
        if (!consumerInstance) return;

        const eventParsed = parseJson(event?.data) as TWsMessage;

        handleMessageEvent(consumerInstance, eventParsed)
          .then(() => {
            initialMessageEvent.call(consumerInstance?.connection, event);
          })
          .catch((error) => {
            logger.warn('[handleMessageEvent] error: ', error);
          });
      };

      consumerInstance.connect();
    }
  } catch (error) {
    return Promise.reject(error);
  }

  return consumerInstance as TCable;
};

/** Получить идентификатор подписки на канал. Формируется по такому же принципу, как subscription identifier в библиотеке actionCable */
const getSubscriptionIdentifier = (channelParams: TWsChannelNameWithParams): string => JSON.stringify(channelParams);

/** Ищет подписку на канал */
const findSubscription = (channelParams: TWsChannelNameWithParams): TCableSubscription | null => {
  if (!consumerInstance) return null;

  const subscriptionIdentifier = getSubscriptionIdentifier(channelParams);

  return consumerInstance.subscriptions.subscriptions.find(
    ({ identifier }: TCableSubscription) => identifier === subscriptionIdentifier,
  ) || null;
};

/** Возвращает подписку на канал, если ранее подключались, в противном случае создает подписку и возвращает */
const getSubscription = ({
  cableConsumer,
  channelParams,
  subscriptionHandlers,
}: {
  cableConsumer: TCable,
  channelParams: TWsChannelNameWithParams,
  subscriptionHandlers: TWsSubscriptionHandlers,
}): TCableSubscription => {
  const subscription = findSubscription(channelParams);

  return subscription || cableConsumer.subscriptions.create(
    channelParams, subscriptionHandlers,
  ) as unknown as TCableSubscription;
};

/**
 * Подключаемся к каналу (создаем подписку или возвращаем ранее созданную)
 *
 * Аргументы:
 * 1. channelParams - используется для создания подписки на канал, содержит название канала и доп параметры (к примеру, employeeId);
 * 2. subscriptionConsumer - потребитель подписки на канал, содержит методы вызываемые при получении сообщений или при отписке.
 */
export const createChannel = ({ channelParams, subscriptionConsumer }:
{
  channelParams: TWsChannelNameWithParams,
  subscriptionConsumer: TWsSubscriptionConsumer,
}) => {
  const subscriptionIdentifier = getSubscriptionIdentifier(channelParams);

  const initialized = () => {
    logger.log(`Channel "${channelParams.channel}" successfully initialized.`);
  };

  const connected = () => {
    logger.log(`Channel "${channelParams.channel}" successfully connected.`);
  };

  const received = (data: unknown) => {
    const parsed = parseJson(data);
    logger.log(`Message from channel "${channelParams.channel}": `, parsed);

    // вызываем обработчик сообщения каждого потребителя подписки на канал
    subscriptionConsumers[subscriptionIdentifier]?.forEach(({ handleMessage }: TWsSubscriptionConsumer) => {
      handleMessage(parsed);
    });
  };

  const disconnected = () => {
    logger.log(`Channel "${channelParams.channel}" successfully disconnected.`);
  };

  const rejected = () => {
    resetConsumersForSubscription(subscriptionIdentifier);
  };

  return getConsumerInstance()
    .then(async (consumer: TCable) => {
      /**
       * await getTasksStackPromise – ждём, пока выполнятся все внутренние функции контроллера,
       * и только потом выполняем код подписки на канал.
       * Внутренняя функция – например, функция reconnectWithNewUrl для подключения с новым tenantId
       */
      await getTasksStackPromise();

      // подписка на канал
      const subscription = getSubscription({
        cableConsumer: consumer,
        channelParams,
        subscriptionHandlers: {
          initialized,
          connected,
          received,
          disconnected,
          rejected,
        },
      });

      /** Исходный метод отписки от канала */
      const initialUnsubscribe = subscription.unsubscribe;

      addConsumerForSubscription(subscriptionIdentifier, subscriptionConsumer);

      /**
       * Так как subscription.unsubscribe() может быть вызван не только самим потребителем подписки,
       * но и к примеру при смене тенанта в disconnectConsumer, то необходимо выполнить сброс внутреннего состояния потребителя подписки
      */
      subscription.unsubscribe = () => {
        resetConsumersForSubscription(subscriptionIdentifier);

        initialUnsubscribe.call(subscription);
      };

      const unsubscribe = () => {
        if (!subscriptionConsumers[subscriptionIdentifier]?.length) {
          initialUnsubscribe.call(subscription);

          return;
        }

        removeConsumerForSubscription(subscriptionIdentifier, subscriptionConsumer.id);

        // если не осталось потребителей подписки, то отписываемся от канала
        if (!subscriptionConsumers[subscriptionIdentifier]?.length) {
          initialUnsubscribe.call(subscription);
        }
      };

      const initialErrorEvent = consumer.connection.events.error;
      /* eslint-disable-next-line no-param-reassign */
      consumer.connection.events.error = (err: unknown) => {
        logger.log(channelParams.channel, 'WebSocket error: ', err);
        initialErrorEvent.call(consumer.connection, err);
      };

      return unsubscribe;
    })
    .catch((error: { message: string }) => {
      logger.warn('[createChannel] Error occurred while trying to create consumer. Error: ', error.message);
    });
};
